import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useLazyQuery } from '@apollo/client';

import { FormattedMessage } from 'react-intl';
import AgentPortalError from 'errors/AgentPortalError';
import { ACTION_CODES } from 'constants/errors';
import { bookingShape, historyShape } from 'constants/propTypes';
import { BOOKING_WINDOW_MINUTES } from 'constants/booking';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { STD, SP, BP, ER_STANDARD, STANDARD, COMFORT, PLUS, PREMIER } from 'constants/classOfService';
import Modal from 'components/ui/Modal';
import { ConfirmButton } from 'components/ui/Button';
import { Spinner } from 'admin-components/components';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import { GET_IMPULSE_UPGRADE_SEARCH } from 'store/queries';
import { IMPULSE_UPGRADE_MUTATION } from 'store/mutations';
import ChildrenWillTravelAloneWarning from '../../AfterSalesWarning/ChildrenWillTravelAloneWarning/ChildrenWillTravelAloneWarning';

import {
  IMPULSE_FROM_STD_TO_SP,
  IMPULSE_FROM_STD_TO_BP,
  IMPULSE_FROM_SP_TO_BP,
  getFares,
  getFaresAvailability
} from './impulseUpgrade.utils';

import { StyledImpulseupgrade, StyledImpulseupgradeSPButton } from './impulseUpgradeCta.styles.js';

import {
  runAfterSaleValidator,
  bookingIsConfirmed,
  oneSegmentSelected,
  allSegmentsDepartToday,
  allSegmentsOfType,
  allSegmentsAreMinutesBeforeDeparture,
  getSegmentsForItemRefs,
  allSegmentsOfService,
  allSelectedProductsConfirmed,
  passengersWithMixedJourneysInSegments,
  canceledPassengersInSegments,
  selectedItemsHaveLuggageExtras,
  getFaresForPassengersInMixedJourney,
  selectedProductsHaveDifferentProductFamily,
  getProductFamiliesPerPassengersInMixedPF
} from '../AfterSalesCtas.utils';
import { StyledCtaButtons } from '../AfterSalesCtas.styles';
import WarningCta from '../WarningCta/WarningCta';
import BlockTicketsWithExtraLuggageWarning from '../../AfterSalesWarning/BlockTicketsWithExtraLuggageWarning/BlockTicketsWithExtraLuggageWarning';

const ImpulseUpgradeCta = ({
  label,
  booking,
  booking: { reference, currency, isLoyalty },
  selectedItemRefs,
  history: { push },
  willResultInChildAlone = false,
  isMenuOpen = false,
  disabled,
  setDisabled
}) => {
  const [blockAftersalesAsChildrenWillTravelAlone, setBlockAftersalesAsChildrenWillTravelAlone] = useState(false);
  const [impulseUpgradeInfos, { data: impulseUpgradeSearch, loading: impulseUpgradeSearchLoading, error: impulseUpgradeSearchError }] =
    useLazyQuery(GET_IMPULSE_UPGRADE_SEARCH, {
      fetchPolicy: 'network-only'
    });

  const [isOpen, setOpen] = useState(false);
  const [showBlockTicketsWithExtraLuggageWarning, setShowBlockTicketsWithExtraLuggageWarning] = useState(false);

  const hasLuggageExtras = selectedItemsHaveLuggageExtras(booking, selectedItemRefs);

  useEffect(() => {
    setDisabled(
      !runAfterSaleValidator(
        booking,
        selectedItemRefs
      )([
        bookingIsConfirmed,
        oneSegmentSelected,
        allSelectedProductsConfirmed,
        allSegmentsDepartToday,
        allSegmentsOfType([STD, ER_STANDARD, STANDARD, SP, COMFORT, PLUS]),
        allSegmentsOfService([serviceTypeCodes.EUROSTAR]),
        allSegmentsAreMinutesBeforeDeparture(BOOKING_WINDOW_MINUTES)
      ]) || isLoyalty
    );
  });

  const hasPassengersWithMixedJourneys = runAfterSaleValidator(
    booking,
    selectedItemRefs,
    null,
    'some'
  )([canceledPassengersInSegments, passengersWithMixedJourneysInSegments]);

  const faresForPassengersInMixedJourney = getFaresForPassengersInMixedJourney({
    booking,
    selectedItemRefs,
    hasPassengersWithMixedJourneys
  });

  const hasMoreThanOneProductFamily = runAfterSaleValidator(booking, selectedItemRefs)([selectedProductsHaveDifferentProductFamily]);

  const productFamiliesPerPassengersInMixedPF = getProductFamiliesPerPassengersInMixedPF({
    booking,
    selectedItemRefs,
    hasMoreThanOneProductFamily
  });

  const segments = Array.from(getSegmentsForItemRefs(booking, selectedItemRefs));
  const segmentDirection = segments?.[0]?.direction;

  const standardTickets = runAfterSaleValidator(booking, selectedItemRefs)([allSegmentsOfType([STD, ER_STANDARD, STANDARD])]);

  const standardPremierTickets = runAfterSaleValidator(booking, selectedItemRefs)([allSegmentsOfType([SP, COMFORT, PLUS])]);

  const handleToggle = async (triggerSeach) => {
    if (willResultInChildAlone) {
      return setBlockAftersalesAsChildrenWillTravelAlone(true);
    }

    if (triggerSeach) {
      await impulseUpgradeInfos({
        variables: {
          reference,
          currency,
          exchangeItems: selectedItemRefs.map(Number)
        }
      });
    }

    setOpen(!isOpen);
  };

  const [impulseUpgrade, { loading, error: impulseUpgradeError }] = useMutation(IMPULSE_UPGRADE_MUTATION);

  if (impulseUpgradeError || impulseUpgradeSearchError) {
    throw new AgentPortalError(impulseUpgradeError || impulseUpgradeSearchError, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: `/manage/bookings/${reference}/journeys`
    });
  }

  const handleImpulseUpgrade = async (fromSTDtoSP, fromSTDtoBP, fromSPtoBP) => {
    if (hasLuggageExtras) {
      setShowBlockTicketsWithExtraLuggageWarning(true);
      return;
    }

    await impulseUpgrade({
      variables: {
        reference,
        currency,
        exchangeItems: selectedItemRefs.map(Number),
        fromSTDtoSP,
        fromSTDtoBP,
        fromSPtoBP
      }
    });
    push(`/exchange/${reference}/confirm?isImpulseUpgrade=true`);
  };

  const plusAvailability = getFaresAvailability(impulseUpgradeSearch, segmentDirection, [SP, PLUS]);
  const premierAvailability = getFaresAvailability(impulseUpgradeSearch, segmentDirection, [BP, PREMIER]);

  const plusPrice = plusAvailability
    ? getFares(plusAvailability, IMPULSE_FROM_STD_TO_SP, impulseUpgradeSearch?.impulseUpgradeJourneySearch?.passengers)
    : null;
  const premierPrice = premierAvailability
    ? getFares(
        premierAvailability,
        standardPremierTickets ? IMPULSE_FROM_SP_TO_BP : IMPULSE_FROM_STD_TO_BP,
        impulseUpgradeSearch?.impulseUpgradeJourneySearch?.passengers
      )
    : null;

  const toggleBlockTicketsWithExtraLuggageWarning = () => {
    setShowBlockTicketsWithExtraLuggageWarning(!showBlockTicketsWithExtraLuggageWarning);
  };

  if (blockAftersalesAsChildrenWillTravelAlone) {
    return <ChildrenWillTravelAloneWarning isOpen={true} closeModal={() => setBlockAftersalesAsChildrenWillTravelAlone(false)} />;
  }

  return (
    <>
      {(hasPassengersWithMixedJourneys || hasMoreThanOneProductFamily) && !disabled ? (
        <WarningCta
          label={label}
          title={<FormattedMessage id="afterSales.warning" />}
          subtitle={<FormattedMessage id="afterSales.warningSelectionMessage" />}
          hasPassengersWithMixedJourneys={hasPassengersWithMixedJourneys}
          faresForPassengersInMixedJourney={faresForPassengersInMixedJourney}
          hasMoreThanOneProductFamily={hasMoreThanOneProductFamily}
          productFamiliesPerPassengersInMixedPF={productFamiliesPerPassengersInMixedPF}
          isMenuOpen={isMenuOpen}
        />
      ) : (
        <StyledCtaButtons
          data-cy="impulse-upgrade-cta"
          label={label}
          disabled={loading || disabled}
          onClick={() => handleToggle(true)}
          loading={loading}
          isMenuOpen={isMenuOpen}
        />
      )}
      <Modal isOpen={isOpen} title={<FormattedMessage id="afterSales.impulseUpgrade" />} onClose={() => handleToggle(false)}>
        <FormattedMessage id="afterSales.confirmImpulseUpgrade" />
        {impulseUpgradeSearchLoading ? (
          <Spinner />
        ) : (
          <StyledImpulseupgrade>
            {standardTickets && (
              <StyledImpulseupgradeSPButton>
                <ConfirmButton
                  data-testid="sp-impulse-upgrade-button"
                  data-cy="sp-impulse-upgrade-button"
                  onClick={() => handleImpulseUpgrade(true)}
                  loading={loading}
                  disabled={!plusPrice || loading}
                >
                  {plusPrice ? (
                    <FormattedMessage
                      id="aftersales.spAvailableImpulseUpgrade"
                      values={{
                        price: <FormattedCurrency className="formatted-currency--impulse" currency={currency} amount={plusPrice} />
                      }}
                    />
                  ) : (
                    <FormattedMessage id="aftersales.spNotAvailableImulseUpgrade" />
                  )}
                </ConfirmButton>
              </StyledImpulseupgradeSPButton>
            )}

            <ConfirmButton
              data-testid="bp-impulse-upgrade-button"
              data-cy="bp-impulse-upgrade-button"
              onClick={() => handleImpulseUpgrade(false, standardTickets, standardPremierTickets)}
              loading={loading}
              disabled={!premierPrice || loading}
            >
              {premierPrice ? (
                <FormattedMessage
                  id="aftersales.bpAvailableImpulseUpgrade"
                  values={{
                    price: <FormattedCurrency className="formatted-currency--impulse" currency={currency} amount={premierPrice} />
                  }}
                />
              ) : (
                <FormattedMessage id="aftersales.bpNotAvailableImpulseUpgrade" />
              )}
            </ConfirmButton>
          </StyledImpulseupgrade>
        )}
      </Modal>

      <BlockTicketsWithExtraLuggageWarning
        isOpen={showBlockTicketsWithExtraLuggageWarning}
        closeModal={toggleBlockTicketsWithExtraLuggageWarning}
      />
    </>
  );
};

ImpulseUpgradeCta.propTypes = {
  label: PropTypes.node,
  disabled: PropTypes.bool,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string),
  booking: bookingShape,
  history: historyShape,
  willResultInChildAlone: PropTypes.bool,
  isMenuOpen: PropTypes.bool,
  setDisabled: PropTypes.func
};

export default ImpulseUpgradeCta;
